/**
 * Game-clock scheduler for turn flow.
 *
 * Everything that used to be a wall-clock `setTimeout` or a 100 ms poll in the
 * turn sequence (post-shot pause, wait for landings, bot thinking delay, bot
 * action pacing) runs here instead, advanced once per frame by `tick(dt)`.
 *
 * Why: timers fired in an order that depended on frame rate and on timers
 * leaking out of an abandoned game ("skip bug", "double turn"); fast-forward
 * could not speed them up; and nothing could test the sequence. Here time is
 * whatever the game loop says it is, ordering is by due time then scheduling
 * order, `clear()` drops everything at once, and tests drive it with `tick`.
 *
 * Pure TypeScript, no DOM: the game loop owns the clock.
 */

interface Timer { id: number; at: number; fn: () => void }
interface Waiter { pred: () => boolean; resolve: () => void }

export class FrameScheduler {
  private now = 0;
  private nextId = 1;
  private timers: Timer[] = [];
  private waiters: Waiter[] = [];
  private frameWaiters: Array<() => void> = [];
  private generation = 0; // bumped by clear() so a tick in progress stops firing

  /** Game time elapsed, in seconds. */
  get time(): number { return this.now; }

  /** Timers and waiters still outstanding. */
  get pending(): number { return this.timers.length + this.waiters.length + this.frameWaiters.length; }

  /**
   * Run `fn` once `seconds` of game time have passed (on the first tick at or
   * past the due time). Returns a cancel function.
   */
  after(seconds: number, fn: () => void): () => void {
    const timer: Timer = { id: this.nextId++, at: this.now + Math.max(0, seconds), fn };
    this.timers.push(timer);
    return () => { this.timers = this.timers.filter(t => t !== timer); };
  }

  /** Promise form of `after`. */
  delay(seconds: number): Promise<void> {
    return new Promise(resolve => { this.after(seconds, resolve); });
  }

  /** Resolves on the first tick where `pred` holds (immediately if it already does). */
  waitUntil(pred: () => boolean): Promise<void> {
    if (pred()) return Promise.resolve();
    return new Promise(resolve => { this.waiters.push({ pred, resolve }); });
  }

  /** Resolves on the next tick. */
  nextFrame(): Promise<void> {
    return new Promise(resolve => { this.frameWaiters.push(resolve); });
  }

  /**
   * Advance game time by `dt` seconds and fire what is due, in order: due
   * timers (by due time, then scheduling order), then condition waiters, then
   * next-frame waiters. Anything scheduled from inside a callback waits for the
   * next tick, so a tick can never loop on itself; `clear()` from inside a
   * callback stops the rest of the tick.
   */
  tick(dt: number): void {
    this.now += Math.max(0, dt);

    const due = this.timers.filter(t => t.at <= this.now).sort((a, b) => a.at - b.at || a.id - b.id);
    if (due.length > 0) {
      const dueSet = new Set(due);
      this.timers = this.timers.filter(t => !dueSet.has(t));
      const gen = this.generation;
      for (const t of due) {
        if (this.generation !== gen) return; // clear() ran inside a callback (e.g. game over): stop this tick
        t.fn();
      }
    }

    if (this.waiters.length > 0) {
      const checking = this.waiters;
      this.waiters = [];
      const still: Waiter[] = [];
      const gen = this.generation;
      for (const w of checking) {
        if (this.generation !== gen) return;
        if (w.pred()) w.resolve(); else still.push(w);
      }
      this.waiters = [...still, ...this.waiters];
    }

    if (this.frameWaiters.length > 0) {
      const ready = this.frameWaiters;
      this.frameWaiters = [];
      for (const r of ready) r();
    }
  }

  /** Drop every pending timer and waiter (new game, game over). Pending promises never resolve. */
  clear(): void {
    this.generation++;
    this.timers = [];
    this.waiters = [];
    this.frameWaiters = [];
  }
}
